import { useState } from 'react';

export enum PAGINATION_LIMIT_PER_PAGE {
	'x1' = 1,
	'x25' = 25,
	'x30' = 30,
	'x50' = 50,
	'x100' = 100,
}

export interface PAGINATION_STATE {
	offset: number;
	limit: PAGINATION_LIMIT_PER_PAGE;
	totalNumberOfItems: number;
}
const defaultState: PAGINATION_STATE = Object.freeze({
	offset: 0,
	limit: PAGINATION_LIMIT_PER_PAGE.x25,
	totalNumberOfItems: 10,
});
export const usePagination = (initialState: PAGINATION_STATE = defaultState) => {
	const [state, setState] = useState<PAGINATION_STATE>({
		...initialState,
	});
	const onSpecificPageClick = (page: number) => {
		setState({
			...state,
			offset: page * state.limit,
		});
	};

	const onNextPage = () => {
		const newOffset = Math.min(
			state.offset + state.limit,
			(Math.ceil(state.totalNumberOfItems / state.limit) - 1) * state.limit
		);
		setState({
			...state,
			offset: newOffset,
		});
	};
	const onPrevPage = () => {
		setState({
			...state,
			offset: Math.max(state.offset - state.limit, 0),
		});
	};
	const onFirstPage = () => {
		setState({
			...state,
			offset: 0,
		});
	};
	const onLastPage = () => {
		setState({
			...state,
			offset: (Math.ceil(state.totalNumberOfItems / state.limit) - 1) * state.limit,
		});
	};

	const onLimitChange = (newLimit: number) => {
		if (newLimit === state.limit) {
			return;
		}
		setState({
			...state,
			offset: initialState.offset,
			limit: newLimit,
		});
	};

	const onPageChange = (newPage: number) => {
		const newOffset = newPage * state.limit;
		setState({
			...state,
			offset: newOffset,
		});
	};

	const onTotalNumberOfItemsChange = (newTotalNumber: number) => {
		if (newTotalNumber === state.totalNumberOfItems) {
			return;
		}
		setState({
			...state,
			offset: initialState.offset,
			totalNumberOfItems: newTotalNumber,
		});
	};

	const resetOffset = () => {
		setState({
			...state,
			offset: 0,
		});
	};

	const totalPages = Math.ceil(Math.max(state.totalNumberOfItems / state.limit, 1));
	const currentPage = Math.ceil(state.offset / state.limit);

	return {
		state,
		resetOffset,
		onPageChange,
		onLimitChange,
		currentPage,
		totalPages,
		onTotalNumberOfItemsChange,
		onNextPage,
		onPrevPage,
		onFirstPage,
		onLastPage,
		onSpecificPageClick,
	};
};
